/**
 * Data layer for `api-rate-limiting`. Thin typed fetch wrappers over
 * /api/v1/api-rate-limiting. Raw `fetch()` is confined to this file.
 */

export interface RateLimitsBlock {
	rpm: number;
	rph: number;
	rpd: number;
	rpm_default: boolean;
	rph_default: boolean;
	rpd_default: boolean;
}

export interface StoredOverrides {
	rpm: number | null;
	rph: number | null;
	rpd: number | null;
}

export interface LimitsResponse {
	tenant_id: string;
	api_key_id: string;
	limits: RateLimitsBlock;
	stored: StoredOverrides;
	has_override: boolean;
	defaults: { rpm: number; rph: number; rpd: number };
}

export interface WindowUsageRow {
	window: "m" | "h" | "d";
	bucket: number;
	count: number;
	limit: number;
	remaining: number;
	reset_at: number;
}

export interface UsageResponse {
	tenant_id: string;
	api_key_id: string;
	now: number;
	limits: { rpm: number; rph: number; rpd: number };
	windows: WindowUsageRow[];
	remaining: number;
	reset_at: number;
}

export interface SaveOverridesBody {
	api_key_id: string;
	rpm?: number | null;
	rph?: number | null;
	rpd?: number | null;
}

export interface SaveOverridesResponse {
	tenant_id: string;
	api_key_id: string;
	limits: RateLimitsBlock;
	stored: StoredOverrides;
	updated_by: string | null;
	updated_at: number;
}

const BASE = "/api/v1/api-rate-limiting";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getLimits(apiKeyId: string): Promise<LimitsResponse> {
	const res = await fetch(`${BASE}/limits/${encodeURIComponent(apiKeyId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<LimitsResponse>(res);
}

export async function getUsage(apiKeyId: string): Promise<UsageResponse> {
	const res = await fetch(`${BASE}/usage/${encodeURIComponent(apiKeyId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<UsageResponse>(res);
}

export async function saveOverrides(
	body: SaveOverridesBody,
): Promise<SaveOverridesResponse> {
	const res = await fetch(`${BASE}/limits`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<SaveOverridesResponse>(res);
}

export async function clearOverrides(
	apiKeyId: string,
): Promise<LimitsResponse> {
	const res = await fetch(`${BASE}/limits/${encodeURIComponent(apiKeyId)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<LimitsResponse>(res);
}

export const apiRateLimitingQueryKeys = {
	all: ["api-rate-limiting"] as const,
	limits: (apiKeyId: string) =>
		[...apiRateLimitingQueryKeys.all, "limits", apiKeyId] as const,
	usage: (apiKeyId: string) =>
		[...apiRateLimitingQueryKeys.all, "usage", apiKeyId] as const,
};
